/**
 * Currency Hook for multi-currency Stripe payments
 */
import { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL as API_URL } from '@/lib/backendUrl';
import { paymentSourceFields } from '@/lib/paymentProvider';
import { singleCheckout } from '@/lib/checkoutGuard';
// Currency symbols
export const CURRENCY_SYMBOLS = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  CAD: 'C$',
  AUD: 'A$',
  THB: '฿',
  ILS: '₪',
  UAH: '₴',
  KZT: '₸',
};

// Currency display names
export const CURRENCY_NAMES = {
  USD: 'US Dollar',
  EUR: 'Euro',
  GBP: 'British Pound',
  CAD: 'Canadian Dollar',
  AUD: 'Australian Dollar',
  THB: 'Thai Baht',
  ILS: 'Israeli Shekel',
  UAH: 'Ukrainian Hryvnia',
  KZT: 'Kazakhstani Tenge',
};

const STORAGE_KEY = 'user_preferred_currency';

export function useCurrency() {
  const [currency, setCurrency] = useState(() => {
    // Check localStorage first
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved || 'USD';
  });
  const [prices, setPrices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch prices from backend.
  //
  // The backend /api/stripe/prices endpoint requires authentication. Anonymous
  // visitors on /pricing don't need live Stripe prices — we simply fall back
  // to the built-in RUB list. Skipping the fetch also keeps the preview/console
  // clean of spurious 401 errors that confuse QA / preview readers.
  const fetchPrices = useCallback(async () => {
    const token = typeof window !== 'undefined'
      ? window.localStorage.getItem('auth_token')
      : null;

    // No auth token → no live prices available. Stay in "fallback" state
    // silently instead of generating a console error.
    if (!token) {
      setLoading(false);
      setPrices(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const res = await fetch(`${API_URL}/api/stripe/prices`, { headers });

      if (!res.ok) {
        throw new Error('Failed to fetch prices');
      }

      const data = await res.json();

      // Update currency from server detection if no user preference
      const savedCurrency = localStorage.getItem(STORAGE_KEY);
      if (!savedCurrency && data.currency) {
        setCurrency(data.currency);
      }

      setPrices(data);
    } catch (err) {
      console.error('[useCurrency] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Save currency preference
  const saveCurrency = useCallback((newCurrency) => {
    setCurrency(newCurrency);
    localStorage.setItem(STORAGE_KEY, newCurrency);
  }, []);

  // Clear preference (use auto-detection)
  const clearPreference = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    fetchPrices(); // Re-fetch to get auto-detected currency
  }, [fetchPrices]);

  // Format price with currency symbol
  const formatPrice = useCallback((amount, curr = currency) => {
    const symbol = CURRENCY_SYMBOLS[curr] || curr;
    return `${symbol}${amount}`;
  }, [currency]);

  // Get price for a plan
  const getPlanPrice = useCallback((plan) => {
    if (!prices?.plans?.[plan]) return null;
    
    const planData = prices.plans[plan];
    return {
      amount: planData.amount,
      formatted: planData.formatted || formatPrice(planData.amount),
      fallback: planData.fallback || false,
      priceId: planData.price_id,
    };
  }, [prices, formatPrice]);

  useEffect(() => {
    fetchPrices();
  }, [fetchPrices]);

  return {
    currency,
    setCurrency: saveCurrency,
    clearPreference,
    prices,
    loading,
    error,
    formatPrice,
    getPlanPrice,
    symbol: CURRENCY_SYMBOLS[currency] || currency,
    availableCurrencies: Object.keys(CURRENCY_SYMBOLS),
    refetch: fetchPrices,
  };
}

/**
 * Create multi-currency checkout
 */
export function createMultiCurrencyCheckout(plan, originUrl, preferredCurrency = null) {
  // Одна покупка за раз — повторный тап не заводит вторую сессию Stripe.
  return singleCheckout(() => createMultiCurrencyCheckoutNow(plan, originUrl, preferredCurrency),
    (data) => !!(data && data.url));
}

async function createMultiCurrencyCheckoutNow(plan, originUrl, preferredCurrency = null) {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    throw new Error('Требуется авторизация');
  }

  const res = await fetch(`${API_URL}/api/stripe/checkout-multicurrency`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      plan,
      origin_url: originUrl,
      preferred_currency: preferredCurrency,
      // Источник покупки: UTM визита, referrer, ClientID Метрики.
      ...(await paymentSourceFields()),
    }),
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data.detail || 'Ошибка создания платежа');
  }

  return data;
}
